"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL } from "../config/apiconfig";

const ItemContext = createContext(null);

export const ItemProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ITEM_ENDPOINT = `${BASE_URL}/items`;

  /* ---------- AUTH HEADERS ---------- */
  const getHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  /* ---------- NORMALIZE ---------- */
  const normalizeItem = (item) => ({
    id: item.id,
    item: item.item ?? "",
    status: item.status ?? "active",
  });

  /* ---------- FETCH ---------- */
  const fetchItems = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(ITEM_ENDPOINT, {
        headers: getHeaders(),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch items");
      }

      const json = await res.json();

      let list = [];

      if (Array.isArray(json)) {
        list = json;
      } else if (Array.isArray(json.data)) {
        list = json.data;
      } else if (json && typeof json === "object" && json.id) {
        list = [json];
      }

      setItems(list.map(normalizeItem));
    } catch (err) {
      console.error(err);
      setItems([]);
      setError("Failed to load items");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------- ADD ---------- */
  const addItem = async (item) => {
  try {
    const res = await fetch(ITEM_ENDPOINT, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        item,
        status: "active", // ✅ lowercase
      }),
    });

    if (!res.ok) throw new Error("Add failed");

    const json = await res.json();
    const data = json?.data ?? json;

    setItems((prev) => [normalizeItem(data), ...prev]);
  } catch (err) {
    console.error("Add failed", err);
  }
};

  /* ---------- UPDATE ---------- */
  const updateItem = async (id, item) => {
  try {
    const current = items.find((i) => i.id === id);

    const res = await fetch(`${ITEM_ENDPOINT}/${id}`, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({
        item,
        status: current?.status ?? "active", // ✅ keep existing
      }),
    });

    if (!res.ok) throw new Error("Update failed");

    const json = await res.json();
    const data = json?.data ?? json;

    setItems((prev) =>
      prev.map((i) =>
        i.id === id ? normalizeItem(data) : i
      )
    );
  } catch (err) {
    console.error("Update failed", err);
  }
};

  /* ---------- TOGGLE STATUS ---------- */
  const toggleStatus = async (id) => {
    const current = items.find((i) => i.id === id);
    if (!current) return;

    const newStatus =
      current.status === "active" ? "inactive" : "active";

    try {
      await fetch(`${ITEM_ENDPOINT}/${id}/status`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });

      setItems((prev) =>
        prev.map((i) =>
          i.id === id ? { ...i, status: newStatus } : i
        )
      );
    } catch (err) {
      console.error("Status toggle failed", err);
    }
  };

  /* ---------- DELETE ---------- */
  const deleteItem = async (id) => {
    try {
      await fetch(`${ITEM_ENDPOINT}/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* ---------- INITIAL LOAD ---------- */
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <ItemContext.Provider
      value={{
        items,
        loading,
        error,
        fetchItems,
        addItem,
        updateItem,
        toggleStatus,
        deleteItem,
      }}
    >
      {children}
    </ItemContext.Provider>
  );
};

export const useItem = () => {
  const ctx = useContext(ItemContext);
  if (!ctx) {
    throw new Error("useItem must be used inside ItemProvider");
  }
  return ctx;
};